import type { LocaleFormat } from '@components'

import type { TFunction } from 'i18next'
import type { FieldValues, Path, PathValue, UseFormGetValues, UseFormSetValue } from 'react-hook-form'

import { defaultInvoiceTextTranslationKeys } from '@features/invoices/constants/defaultInvoiceTextTranslationKeys'

interface SyncDefaultInvoiceTextsParams<T extends FieldValues & { message: string; footerNote: string }> {
  getValues: UseFormGetValues<T>
  newLanguage?: LocaleFormat
  previousLanguage?: LocaleFormat
  setValue: UseFormSetValue<T>
  t: TFunction
}

// When the Invoice language changes, automatically retranslates message and footerNote
// only if they still hold the default value — preserving any user customization.
export const syncDefaultInvoiceTextsOnLanguageChange = <
  T extends FieldValues & { message: string; footerNote: string },
>({
  getValues,
  newLanguage,
  previousLanguage,
  setValue,
  t,
}: SyncDefaultInvoiceTextsParams<T>): void => {
  if (!previousLanguage || !newLanguage || previousLanguage === newLanguage) {
    return
  }

  const fields = ['message', 'footerNote'] as const

  for (const field of fields) {
    const translationKey = defaultInvoiceTextTranslationKeys[field]
    const path = field as Path<T>

    if (getValues(path) === t(translationKey, { lng: previousLanguage })) {
      setValue(path, t(translationKey, { lng: newLanguage }) as PathValue<T, Path<T>>)
    }
  }
}
